import styled, { keyframes } from "styled-components";
import { NavLink } from "react-router-dom";

export const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.lightBlue};
`;

export const HeaderArea = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

export const Content = styled.main`
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(10)} 0
    ${({ theme }) => theme.spacing(16)};
`;

export const Title = styled.h1`
  color: ${({ theme }) => theme.colors.deepDarkBlue};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(6)};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const Navigation = styled.nav`
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.light.small};
`;

export const NavigationLink = styled(NavLink)`
  min-height: 48px;
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.mediumGray};
  text-decoration: none;
  display: flex;
  align-items: center;

  &:hover,
  &.active {
    background: ${({ theme }) => theme.colors.lightBlue};
    color: ${({ theme }) => theme.colors.mediumBlue};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
  }
`;

export const NavigationLogoutButton = styled.button`
  width: 100%;
  min-height: 48px;
  padding: ${({ theme }) => theme.spacing(3)};
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium};
  background: transparent;
  color: ${({ theme }) => theme.colors.red};
  font: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.lightBlue};
    outline: none;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

export const Card = styled.section`
  padding: ${({ theme }) => theme.spacing(7)};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.light.small};
`;

export const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.deepDarkBlue};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const Description = styled.p`
  color: ${({ theme }) => theme.colors.darkGray};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

export const DataList = styled.dl`
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const DataRow = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};

  dt {
    color: ${({ theme }) => theme.colors.darkGray};
    font-size: ${({ theme }) => theme.fontSizes.xs};
  }

  dd {
    color: ${({ theme }) => theme.colors.mediumGray};
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;

export const OptionList = styled.ul`
  list-style: none;
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const Option = styled.li`
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

export const NotificationOption = styled.li`
  min-height: 82px;
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const NotificationText = styled.div`
  strong {
    color: ${({ theme }) => theme.colors.mediumGray};
    display: block;
  }

  span {
    color: ${({ theme }) => theme.colors.darkGray};
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

export const Switch = styled.button<{ $checked: boolean }>`
  position: relative;
  flex: 0 0 auto;
  width: 52px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.mediumBlue : theme.colors.gray};
  cursor: pointer;
  transition: background 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $checked }) => ($checked ? "25px" : "3px")};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.white};
    box-shadow: ${({ theme }) => theme.shadows.light.small};
    transition: left 0.2s ease;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.darkBlue};
    outline-offset: 3px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const LoadingState = styled.div<{ $fullPage?: boolean }>`
  min-height: ${({ $fullPage }) => ($fullPage ? "100vh" : "220px")};
  color: ${({ theme }) => theme.colors.mediumGray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const LoadingSpinner = styled.span`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.veryLightBlue};
  border-top-color: ${({ theme }) => theme.colors.mediumBlue};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1.8s;
  }
`;

export const DangerBox = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.lightRed};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: #fff7f6;
  color: ${({ theme }) => theme.colors.mediumGray};
`;

export const PrivacyContent = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(6)};
`;

export const PrivacyLink = styled.a`
  width: fit-content;
  color: ${({ theme }) => theme.colors.mediumBlue};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};

  &:hover {
    color: ${({ theme }) => theme.colors.darkBlue};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.darkBlue};
    outline-offset: 3px;
  }
`;

export const DeleteAccountSection = styled.section`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.lightRed};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: #fff7f6;
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};

  h3 {
    color: ${({ theme }) => theme.colors.red};
    font-size: ${({ theme }) => theme.fontSizes.md};
  }

  p {
    color: ${({ theme }) => theme.colors.mediumGray};
  }
`;

export const ConfirmationInput = styled.input`
  width: 100%;
  min-height: 48px;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.mediumGray};
  font: inherit;

  &:focus {
    border-color: ${({ theme }) => theme.colors.red};
    outline: 2px solid ${({ theme }) => theme.colors.lightRed};
  }
`;

export const DeleteAccountButton = styled.button`
  width: fit-content;
  min-height: 44px;
  padding: 0 ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.red};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.white};
  font: inherit;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  cursor: pointer;

  &:hover:not(:disabled) {
    filter: brightness(0.9);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.red};
    outline-offset: 3px;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.colors.gray};
    background: ${({ theme }) => theme.colors.lightGray};
    color: ${({ theme }) => theme.colors.disabledGray};
    cursor: not-allowed;
  }
`;

export const Form = styled.form`
  display: grid;
  gap: ${({ theme }) => theme.spacing(5)};
`;

export const Field = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const Label = styled.label`
  color: ${({ theme }) => theme.colors.mediumGray};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

export const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 48px;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.red : theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.mediumGray};
  font: inherit;

  &:focus {
    border-color: ${({ theme }) => theme.colors.mediumBlue};
    outline: 2px solid ${({ theme }) => theme.colors.veryLightBlue};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
    color: ${({ theme }) => theme.colors.disabledGray};
    cursor: not-allowed;
  }
`;

export const Hint = styled.small`
  color: ${({ theme }) => theme.colors.darkGray};
`;

export const ErrorText = styled.small`
  color: ${({ theme }) => theme.colors.red};
`;

export const SaveButton = styled.button`
  width: fit-content;
  min-height: 48px;
  padding: 0 ${({ theme }) => theme.spacing(5)};
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.yellow};
  color: ${({ theme }) => theme.colors.black};
  font: inherit;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  cursor: pointer;

  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
    color: ${({ theme }) => theme.colors.disabledGray};
    cursor: not-allowed;
  }
`;
